// src/components/operationCenter/widgets/YouTubeAlphaWidget.tsx
// YouTube alpha widget showing top analyzed videos

import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { DynamicIcon } from "lucide-react/dynamic";
import type { WidgetProps } from "@/types/dashboard";
import { useGetAllChannels } from "@/hooks/mediaInsights/useMediaInsights";
import { useMultiChannelVideos } from "@/pages/youtube-insights/components/shared";
import { calculateVideoAlpha } from "@/services/queries/mediaInsights/youtube";

const formatPublishedDate = (dateStr?: string): string => {
  if (!dateStr) return "Unknown";
  const date = new Date(dateStr);
  const now = new Date();
  const diffTime = Math.abs(now.getTime() - date.getTime());
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays}d ago`;

  const weeks = Math.floor(diffDays / 7);
  if (diffDays < 30) return `${weeks}w ago`;

  const months = Math.floor(diffDays / 30);
  if (diffDays < 365) return `${months}mo ago`;

  const years = Math.floor(diffDays / 365);
  return `${years}y ago`;
};

const YouTubeAlphaWidget: React.FC<WidgetProps> = () => {
  const { data: channelsData, isLoading: isChannelsLoading } = useGetAllChannels();
  const channels = useMemo(() => channelsData?.data || [], [channelsData]);
  const { allVideos, isLoading: isVideosLoading } = useMultiChannelVideos(channels);

  const topVideos = useMemo(() => {
    if (!allVideos.length) return [];

    const withAlpha = allVideos.map(({ video, channel }) => ({
      video,
      channel,
      alpha: calculateVideoAlpha(video.section_alpha_scores),
    }));

    const scored = withAlpha
      .filter((item) => item.alpha !== null)
      .sort((a, b) => (b.alpha ?? 0) - (a.alpha ?? 0));

    const recent = [...withAlpha].sort((a, b) => {
      const aDate = new Date(a.video.published_at || a.video.upload_date || a.video.created_at || 0).getTime();
      const bDate = new Date(b.video.published_at || b.video.upload_date || b.video.created_at || 0).getTime();
      return bDate - aDate;
    });

    const selected: typeof withAlpha = [];
    scored.forEach((item) => {
      if (selected.length < 4) selected.push(item);
    });

    if (selected.length < 4) {
      recent.forEach((item) => {
        if (selected.length >= 4) return;
        if (!selected.some((sel) => sel.video.video_id === item.video.video_id)) {
          selected.push(item);
        }
      });
    }

    return selected.slice(0, 4);
  }, [allVideos]);

  if (isChannelsLoading || isVideosLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="animate-pulse text-slate-500 text-sm">Loading...</div>
      </div>
    );
  }

  if (topVideos.length === 0) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-slate-500 text-sm gap-2">
        <span>No YouTube analysis found</span>
        <Link to="/app/youtube-insights" className="text-xs text-blue-400 hover:text-blue-300">
          Explore YouTube Insights
        </Link>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-700/30">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-slate-400">
          <DynamicIcon name="sparkles" className="w-3 h-3 text-amber-400" />
          Top Alpha Videos
        </div>
        <Link to="/app/youtube-insights" className="text-[10px] text-blue-400 hover:text-blue-300">
          View all
        </Link>
      </div>

      <div className="flex-1 overflow-y-auto divide-y divide-slate-800/60">
        {topVideos.map(({ video, channel, alpha }) => {
          const appUrl = `/app/youtube-insights?channel=${encodeURIComponent(channel.id)}&video=${encodeURIComponent(video.video_id)}`;
          const tickers = video.tickers || [];
          const alphaPercent = alpha !== null ? Math.round(alpha * 100) : null;

          const sentiment = (() => {
            if (video.overall_sentiment === "bullish") {
              return { label: "Bullish", color: "text-emerald-400", icon: "trending-up" as const };
            }
            if (video.overall_sentiment === "bearish") {
              return { label: "Bearish", color: "text-rose-400", icon: "trending-down" as const };
            }
            if (video.overall_sentiment === "neutral") {
              return { label: "Neutral", color: "text-amber-400", icon: "minus" as const };
            }
            return null;
          })();

          const alphaColor =
            alpha === null ? "text-slate-400" : alpha >= 0.6 ? "text-emerald-400" : alpha <= 0.4 ? "text-slate-400" : "text-amber-400";

          return (
            <Link
              key={video.video_id}
              to={appUrl}
              className="flex gap-3 px-3 py-3 hover:bg-slate-800/40 transition-colors"
            >
              <div className="relative w-24 aspect-video rounded-md overflow-hidden bg-slate-800 flex-shrink-0">
                <img
                  src={video.thumbnail_maxres || `https://img.youtube.com/vi/${video.video_id}/hqdefault.jpg`}
                  alt={video.title}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                <div className="absolute bottom-1 right-1 px-1.5 py-0.5 bg-black/80 rounded text-[10px] text-white">
                  {video.duration || "0:00"}
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs text-slate-200 line-clamp-2 font-medium">
                  {video.title}
                </div>
                <div className="flex items-center gap-2 text-[10px] text-slate-500 mt-1">
                  <span className="truncate">{channel.name}</span>
                  <span className="text-slate-600">•</span>
                  <span>{formatPublishedDate(video.published_at || video.upload_date || video.created_at)}</span>
                </div>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  {alphaPercent !== null && (
                    <div className={`flex items-center gap-1 text-[10px] font-medium ${alphaColor}`}>
                      <DynamicIcon name="sparkles" className="w-3 h-3" />
                      <span>{alphaPercent}%</span>
                    </div>
                  )}
                  {sentiment && (
                    <div className={`flex items-center gap-1 text-[10px] ${sentiment.color}`}>
                      <DynamicIcon name={sentiment.icon} className="w-3 h-3" />
                      <span>{sentiment.label}</span>
                    </div>
                  )}
                  {tickers.length > 0 && (
                    <div className="flex items-center gap-1 text-[10px] text-slate-400">
                      {tickers.slice(0, 2).map((ticker) => (
                        <span key={ticker} className="px-1.5 py-0.5 bg-slate-700/60 rounded">
                          {ticker}
                        </span>
                      ))}
                      {tickers.length > 2 && (
                        <span className="text-slate-500">+{tickers.length - 2}</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
};

export default YouTubeAlphaWidget;
